import React, { useState, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Collapse from '@material-ui/core/Collapse'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'

import Chip from '@material-ui/core/Chip'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Tooltip from '@material-ui/core/Tooltip'
import EnergyAPI from 'config/api/EnergyAPI'


function EnergyAnalyticsPanel({ product, onExpectedRevenue, onWasteData, refreshKey = 0, liveUnallocated = null }) {
    const api = new EnergyAPI()
    const [analytics, setAnalytics] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [showCoverage, setShowCoverage] = useState(false)
    const [showTargets, setShowTargets] = useState(false)
    const [showForecastDetail, setShowForecastDetail] = useState(false)
    const [marketingRerunning, setMarketingRerunning] = useState(false)
    const [slackSending, setSlackSending] = useState(false)
    const [showAllBuyers, setShowAllBuyers] = useState(false)

    useEffect(() => {
        loadAnalytics()
    }, [product, refreshKey])

    async function loadAnalytics() {
        if (!product || product === 'all') {
            setAnalytics(null)
            setLoading(false)
            if (onExpectedRevenue) onExpectedRevenue(null)
            if (onWasteData) onWasteData(null)
            return
        }
        setLoading(true)
        setError(null)
        try {
            const data = await api.getEnergyAnalytics(product)
            setAnalytics(data)
            if (onExpectedRevenue) onExpectedRevenue(data.expectedRevenue || null)
            if (onWasteData) onWasteData({
                totalPredictedWaste: data.totalPredictedWaste ?? data.predictedWaste ?? null,
                wastePercent: data.wastePercent ?? null,
                topWasteCounty: data.topWasteCounty ?? null,
                soleCoverageCounties: (data.buyersNeeded || []).filter(b => b.providersInCounty === 1).map(b => b.county)
            })
        } catch (e) {
            console.error('Failed to load analytics:', e)
            setError('Failed to load analytics data')
            if (onExpectedRevenue) onExpectedRevenue(null)
            if (onWasteData) onWasteData(null)
        }
        setLoading(false)
    }

    async function rerunMarketing() {
        setMarketingRerunning(true)
        try {
            await api.rerunMarketingActions(product)
            await loadAnalytics()
        } catch(e) {
            console.error('Failed to rerun marketing actions:', e)
        }
        setMarketingRerunning(false)
    }

    async function sendMarketingSlack() {
        setSlackSending(true)
        try {
            await api.sendMarketingSlack(product)
        } catch(e) {
            console.error('Failed to send marketing Slack:', e)
        }
        setSlackSending(false)
    }

    if (!product || product === 'all') {
        return (
            <Card style={{ marginTop: '1.5rem' }}>
                <CardContent>
                    <Typography variant="body2" style={{ color: '#8898aa', textAlign: 'center', padding: '1rem' }}>
                        Select a specific product to view allocation analytics
                    </Typography>
                </CardContent>
            </Card>
        )
    }

    if (loading) {
        return (
            <Card style={{ marginTop: '1.5rem' }}>
                <CardContent>
                    <Typography variant="body2" style={{ color: '#8898aa', textAlign: 'center', padding: '1rem' }}>
                        Loading analytics...
                    </Typography>
                </CardContent>
            </Card>
        )
    }

    if (error || !analytics) {
        return (
            <Card style={{ marginTop: '1.5rem' }}>
                <CardContent>
                    <Typography variant="body2" style={{ color: '#f5365c', textAlign: 'center', padding: '1rem' }}>
                        {error || 'No analytics data available. The targets cron may not have run yet.'}
                    </Typography>
                </CardContent>
            </Card>
        )
    }

    const {
        totalCap,
        weeklyCapTotal,
        requiredVolume,
        remainingMarketVolume,
        wasteSoFar,
        additionalWasteToFill,
        totalPredictedWaste,
        marketingTargetTotal,
        remainingCapTotal,
        wastePercent,
        countyAnalysis,
        currentTargets,
        eircodeProviders,
        targetsLastComputed,
        targetsComputedAt,
        targetsWeekStart,
        topWasteCounty,
        expectedRevenue,
        adsNeeded,
        buyersNeeded,
        marketingComputedAt,
        wasteWithAds,
        wasteWithoutAds,
        wasteDelta,
        reducedWaste
    } = analytics

    const lastUpdatedStr = targetsComputedAt
        ? new Date(targetsComputedAt).toLocaleString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
        : null

    const coveredCounties = countyAnalysis.filter(c => c.providerCount > 0 && c.avgLeads > 0)
    const totalAvgLeads = countyAnalysis.filter(c => c.avgLeads > 0).reduce((sum, c) => sum + c.avgLeads, 0)

    // Provider list sorted by coverage (lowest first)
    const providerNames = Object.keys(currentTargets || {}).sort((a, b) => {
        const aCov = currentTargets[a].coverageStrength || 0
        const bCov = currentTargets[b].coverageStrength || 0
        return aCov - bCov
    })

    const totalDelivered = providerNames.reduce((sum, n) => sum + (currentTargets[n].delivered || 0), 0)
    const totalRemaining = providerNames.reduce((sum, n) => sum + (currentTargets[n].remaining || 0), 0)
    const safeWasteSoFar = liveUnallocated !== null ? liveUnallocated : (wasteSoFar ?? 0)
    const safeAdditionalWaste = additionalWasteToFill ?? Math.max(0, (remainingMarketVolume ?? totalRemaining) - totalRemaining)
    const safeTotalPredictedWaste = totalPredictedWaste ?? 0
    const safeMarketingTarget = marketingTargetTotal ?? (totalCap + safeTotalPredictedWaste)
    const safeRemainingCap = remainingCapTotal ?? totalRemaining
    const hasAds = adsNeeded && adsNeeded.length > 0
    const totalAdsVolume = hasAds ? adsNeeded.reduce((sum, a) => sum + (a.extraNeeded || 0), 0) : 0
    const adsWasteWithout = wasteWithoutAds ?? safeTotalPredictedWaste
    const adsWasteWith = wasteWithAds ?? reducedWaste ?? null
    const adsWasteDelta = wasteDelta ?? ((adsWasteWithout != null && adsWasteWith != null) ? (adsWasteWithout - adsWasteWith) : null)
    const hasAdsScenario = hasAds && adsWasteWith != null && adsWasteWithout != null && adsWasteDelta != null && adsWasteDelta > 0
    const hasBuyers = buyersNeeded && buyersNeeded.length > 0
    const wasteColor = safeTotalPredictedWaste > 20 ? '#f5365c' : safeTotalPredictedWaste > 5 ? '#fb6340' : '#2dce89'
    const broadSpend = (weeklyCapTotal || totalCap || 0) + safeTotalPredictedWaste
    const coverageMismatchPct = broadSpend > 0 ? Math.round(safeTotalPredictedWaste / broadSpend * 100) : 0
    const leverage = totalAdsVolume > 0 && adsWasteDelta > 0 ? Math.round(adsWasteDelta / totalAdsVolume) : 0

    // Collect all county names that appear across provider targets (sorted by frequency)
    const allTargetCounties = {}
    for (const name of providerNames) {
        const t = currentTargets[name].targets || {}
        for (const c of Object.keys(t)) {
            allTargetCounties[c] = (allTargetCounties[c] || 0) + 1
        }
    }
    const sortedTargetCounties = Object.keys(allTargetCounties).sort((a, b) => allTargetCounties[b] - allTargetCounties[a])

    return (
        <Box style={{ marginTop: '1.5rem' }}>

            {/* Weekly Marketing Brief — combined waste forecast + marketing actions */}
            <Card style={{ marginBottom: '1rem' }}>
                <CardContent style={{ padding: '20px 24px' }}>
                    {/* Header row */}
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ marginBottom: '16px' }}>
                        <Box>
                            <Typography variant="h6" style={{ fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', color: '#32325d' }}>
                                Weekly Marketing Brief
                            </Typography>
                            <Typography variant="caption" style={{ color: '#adb5bd', fontStyle: 'italic', fontSize: '11px' }}>
                                {marketingComputedAt
                                    ? `Updated ${new Date(marketingComputedAt).toLocaleString('en-IE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                                    : lastUpdatedStr ? `Updated ${lastUpdatedStr}` : 'Updates weekly'}
                            </Typography>
                        </Box>
                        <Box display="flex" style={{ gap: '8px' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={marketingRerunning}
                                onClick={rerunMarketing}
                                style={{ minWidth: '90px', fontSize: '11px', borderColor: '#5e72e4', color: '#5e72e4' }}
                            >
                                {marketingRerunning ? <CircularProgress size={14} style={{ color: '#5e72e4' }} /> : 'Rerun'}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={slackSending}
                                onClick={sendMarketingSlack}
                                style={{ minWidth: '90px', fontSize: '11px', borderColor: '#2dce89', color: '#2dce89' }}
                            >
                                {slackSending ? <CircularProgress size={14} style={{ color: '#2dce89' }} /> : 'Slack'}
                            </Button>
                        </Box>
                    </Box>

                    {/* Hero stat — spend efficiency framing */}
                    <Box display="flex" alignItems="baseline" style={{ marginBottom: '20px', gap: '12px' }}>
                        <Tooltip title={`To fill ${weeklyCapTotal || totalCap} buyer cap, broad ads must generate ~${broadSpend} leads. ${safeTotalPredictedWaste} of those land in counties with no buyer coverage — wasted spend.`} arrow>
                            <Typography variant="h3" style={{ fontWeight: 800, fontSize: '36px', color: wasteColor, cursor: 'help' }}>
                                {safeTotalPredictedWaste}
                            </Typography>
                        </Tooltip>
                        {hasAdsScenario && (
                            <Tooltip title={`County ads reduce wasted broad leads from ${adsWasteWithout} → ${adsWasteWith} (saves ${adsWasteDelta}).${leverage >= 2 ? ` Every targeted ad-lead saves ~${leverage} wasted broad leads.` : ''}`} arrow>
                                <Typography variant="h3" style={{ fontWeight: 800, fontSize: '36px', color: '#11cdef', cursor: 'help' }}>
                                    →&nbsp;{adsWasteWith}
                                </Typography>
                            </Tooltip>
                        )}
                        <Box>
                            <Typography variant="body1" style={{ fontWeight: 600, fontSize: '14px', color: '#32325d' }}>
                                broad leads wasted to coverage mismatch
                            </Typography>
                            <Tooltip title={`${coverageMismatchPct}% of the ~${broadSpend} broad ad spend lands in counties where no buyer has remaining cap. ${safeWasteSoFar} already lost this week, ~${safeAdditionalWaste} more expected by Friday.`} arrow>
                                <Typography variant="caption" style={{ color: '#8898aa', fontSize: '12px', cursor: 'help' }}>
                                    {coverageMismatchPct}% of broad spend &middot; {safeWasteSoFar} lost so far, ~{safeAdditionalWaste} more expected
                                </Typography>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Action Sections */}
                    <Box display="flex" style={{ gap: '24px', flexWrap: 'wrap' }}>
                        {/* Sign Buyers section */}
                        {hasBuyers && (
                            <Box style={{ flex: '1 1 300px' }}>
                                <Box display="flex" alignItems="center" style={{ marginBottom: '8px', gap: '6px' }}>
                                    <Box style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f5365c' }} />
                                    <Tooltip title="Counties where projected lead volume exceeds all current buyers' combined capacity. Signing a buyer here permanently reduces the per-week wasted broad spend in that county." arrow>
                                        <Typography variant="subtitle2" style={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', color: '#f5365c', cursor: 'help' }}>
                                            Sign Buyers / Raise Caps
                                        </Typography>
                                    </Tooltip>
                                </Box>
                                <Box display="flex" flexDirection="column" style={{ gap: '6px' }}>
                                    {buyersNeeded
                                        .sort((a, b) => {
                                            const aSole = a.providersInCounty === 1 ? 1 : 0
                                            const bSole = b.providersInCounty === 1 ? 1 : 0
                                            if (aSole !== bSole) return bSole - aSole
                                            return b.weeklyOverflow - a.weeklyOverflow
                                        })
                                        .slice(0, showAllBuyers ? undefined : 5)
                                        .map(item => {
                                            const isSole = item.providersInCounty === 1
                                            return (
                                        <Tooltip key={item.county} title={`~${item.weeklyOverflow} leads/week are projected to arrive in ${item.county} that no buyer has proportional capacity to absorb. ${item.reason === 'no-coverage' ? 'No buyer covers this county at all.' : `${item.providersInCounty} buyer${item.providersInCounty === 1 ? '' : 's'} cover this county but their combined share of capacity is insufficient.`}${isSole ? ' ⚠ Sole provider coverage — operational risk.' : ''}`} arrow>
                                            <Box style={{ padding: '8px 12px', backgroundColor: isSole ? '#fff3cd' : '#fde7ec', border: `1px solid ${isSole ? '#ffc107' : '#f5365c'}`, borderRadius: '6px', cursor: 'help' }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Typography variant="body2" style={{ fontWeight: 700, fontSize: '13px', color: isSole ? '#856404' : '#d6224a' }}>
                                                        {isSole && '⚠️ '}{item.county}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ fontWeight: 700, fontSize: '12px', color: isSole ? '#856404' : '#f5365c' }}>
                                                        ~{item.weeklyOverflow} leads/wk wasted
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" style={{ display: 'block', fontSize: '11px', color: '#8898aa', marginTop: '2px' }}>
                                                    {item.reason === 'no-coverage'
                                                        ? 'No buyer covers this county'
                                                        : isSole
                                                            ? 'Sole provider — operational risk'
                                                            : `${item.providersInCounty} buyers — need more capacity`}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                            )
                                        })}
                                    {buyersNeeded.length > 5 && (
                                        <Box style={{ textAlign: 'center', marginTop: '4px' }}>
                                            <Button
                                                size="small"
                                                onClick={() => setShowAllBuyers(!showAllBuyers)}
                                                style={{ fontSize: '11px', color: '#f5365c', textTransform: 'none' }}
                                            >
                                                {showAllBuyers ? 'Show less' : `Show ${buyersNeeded.length - 5} more`}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* Run Ads section */}
                        {hasAds && (
                            <Box style={{ flex: '1 1 300px' }}>
                                <Box display="flex" alignItems="center" style={{ marginBottom: '8px', gap: '6px' }}>
                                    <Box style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#11cdef' }} />
                                    <Tooltip title={`Counties where buyer targets exceed projected organic supply. Running targeted ads here bypasses the coverage mismatch — each ad-lead goes directly to a buyer instead of being part of the broad spend.${leverage >= 2 ? ` Every targeted ad-lead saves ~${leverage} wasted broad leads.` : ''}`} arrow>
                                        <Typography variant="subtitle2" style={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', color: '#11cdef', cursor: 'help' }}>
                                            Run County Ads
                                        </Typography>
                                    </Tooltip>
                                </Box>
                                <Box display="flex" flexDirection="column" style={{ gap: '6px' }}>
                                    {adsNeeded.slice(0, 5).map(item => (
                                        <Tooltip key={item.county} title={`Organic supply projected at ~${item.historicalLeadsPerWeek} leads/wk in ${item.county}, but buyer targets need +${item.extraNeeded} more. ${item.providersNeedingFill ? item.providersNeedingFill.map(p => `${p.companyName} needs +${p.shortBy}`).join(', ') : ''}`} arrow>
                                            <Box style={{ padding: '8px 12px', backgroundColor: '#e6f9fc', border: '1px solid #11cdef', borderRadius: '6px', cursor: 'help' }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <Typography variant="body2" style={{ fontWeight: 700, fontSize: '13px', color: '#0fb0d0' }}>
                                                        {item.county}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ fontWeight: 700, fontSize: '12px', color: '#0fb0d0' }}>
                                                        +{item.extraNeeded} leads needed
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" style={{ display: 'block', fontSize: '11px', color: '#8898aa', marginTop: '2px' }}>
                                                    {item.providersNeedingFill && item.providersNeedingFill.slice(0, 3).map(p => p.companyName).join(', ')}
                                                    {item.providersNeedingFill && item.providersNeedingFill.length > 3 ? ` +${item.providersNeedingFill.length - 3} more` : ''}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {!hasAds && !hasBuyers && (
                            <Box style={{ padding: '16px', backgroundColor: '#e8f5e9', borderRadius: '6px', width: '100%' }}>
                                <Typography variant="body2" style={{ color: '#2dce89', fontWeight: 600 }}>
                                    No immediate actions — current buyer coverage and caps are well-matched to projected lead volume.
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Collapsible forecast detail */}
                    <Box style={{ marginTop: '16px', borderTop: '1px solid #e9ecef', paddingTop: '12px' }}>
                        <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={() => setShowForecastDetail(!showForecastDetail)}>
                            <Typography variant="caption" style={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#8898aa' }}>
                                Forecast breakdown
                            </Typography>
                            <IconButton size="small" style={{ padding: '2px', marginLeft: '4px' }}>
                                {showForecastDetail ? <ExpandLessIcon style={{ fontSize: '16px' }} /> : <ExpandMoreIcon style={{ fontSize: '16px' }} />}
                            </IconButton>
                        </Box>
                        <Collapse in={showForecastDetail}>
                            <Box display="flex" flexWrap="wrap" style={{ gap: '16px', marginTop: '10px' }}>
                                <Tooltip title="Sum of all active buyers' effective caps (weekly cap minus surge adjustment). This is the real volume the system is pacing to this week." arrow>
                                    <Box style={{ cursor: 'help' }}>
                                        <Typography variant="caption" style={{ display: 'block', fontSize: '10px', color: '#8898aa', textTransform: 'uppercase' }}>Effective Cap</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 700, fontSize: '15px', color: '#32325d' }}>{totalCap}</Typography>
                                    </Box>
                                </Tooltip>
                                <Tooltip title="Leads already delivered to buyers this week (counted against their effective caps)." arrow>
                                    <Box style={{ cursor: 'help' }}>
                                        <Typography variant="caption" style={{ display: 'block', fontSize: '10px', color: '#8898aa', textTransform: 'uppercase' }}>Delivered</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 700, fontSize: '15px', color: '#32325d' }}>{totalDelivered}</Typography>
                                    </Box>
                                </Tooltip>
                                <Tooltip title="Effective cap still unfilled (total effective cap minus delivered). Leads still needed to satisfy all buyers this week." arrow>
                                    <Box style={{ cursor: 'help' }}>
                                        <Typography variant="caption" style={{ display: 'block', fontSize: '10px', color: '#8898aa', textTransform: 'uppercase' }}>Remaining Cap</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 700, fontSize: '15px', color: '#5e72e4' }}>{safeRemainingCap}</Typography>
                                    </Box>
                                </Tooltip>
                                <Tooltip title="Broad ad leads already generated this week that no buyer could absorb — coverage mismatch spend already lost." arrow>
                                    <Box style={{ cursor: 'help' }}>
                                        <Typography variant="caption" style={{ display: 'block', fontSize: '10px', color: '#8898aa', textTransform: 'uppercase' }}>Lost So Far</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 700, fontSize: '15px', color: safeWasteSoFar > 10 ? '#fb6340' : '#32325d' }}>{safeWasteSoFar}</Typography>
                                    </Box>
                                </Tooltip>
                                <Tooltip title="Additional broad leads we expect to generate between now and Friday that will go unsold — they land in counties where no buyer has remaining cap." arrow>
                                    <Box style={{ cursor: 'help' }}>
                                        <Typography variant="caption" style={{ display: 'block', fontSize: '10px', color: '#8898aa', textTransform: 'uppercase' }}>More Expected</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 700, fontSize: '15px', color: safeAdditionalWaste > 10 ? '#fb6340' : '#32325d' }}>{safeAdditionalWaste}</Typography>
                                    </Box>
                                </Tooltip>
                                <Tooltip title={`Total broad ad spend needed to fill all effective caps: ${totalCap} cap + ${safeTotalPredictedWaste} coverage mismatch waste = ${safeMarketingTarget}. The gap exists because some leads inevitably land in counties without buyer coverage.`} arrow>
                                    <Box style={{ cursor: 'help' }}>
                                        <Typography variant="caption" style={{ display: 'block', fontSize: '10px', color: '#8898aa', textTransform: 'uppercase' }}>Broad Spend Needed</Typography>
                                        <Typography variant="body2" style={{ fontWeight: 700, fontSize: '15px', color: '#5e72e4' }}>{safeMarketingTarget}</Typography>
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Collapse>
                    </Box>
                </CardContent>
            </Card>

            {/* Provider Efficiency with County Chips */}
            {providerNames.length > 0 && (
                <Card style={{ marginBottom: '1rem' }}>
                    <CardHeader
                        title={
                            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ cursor: 'pointer' }}
                                onClick={() => setShowTargets(!showTargets)}>
                                <Box>
                                    <Typography variant="h6" style={{ fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', color: '#32325d' }}>
                                        Provider Targets
                                    </Typography>
                                    <Typography variant="caption" style={{ color: '#adb5bd' }}>
                                        Per-county allocation targets
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
                                    <Typography variant="caption" style={{ color: '#adb5bd', fontStyle: 'italic', fontSize: '11px' }}>
                                        {lastUpdatedStr ? `Last updated: ${lastUpdatedStr}` : 'Updates nightly'}
                                    </Typography>
                                    <IconButton size="small">
                                        {showTargets ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>
                            </Box>
                        }
                    />
                    <Collapse in={showTargets}>
                        <CardContent style={{ padding: '24px 24px 20px', overflowX: 'auto' }}>
                            <Box display="flex" alignItems="center" style={{ gap: '12px', marginBottom: '12px', padding: '6px 0' }}>
                                <Box display="flex" alignItems="center" style={{ gap: '4px' }}>
                                    <Box style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: '#e8f5e9' }} />
                                    <Typography variant="caption" style={{ fontSize: '11px', color: '#8898aa' }}>5+ leads</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" style={{ gap: '4px' }}>
                                    <Box style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: '#fff8e1' }} />
                                    <Typography variant="caption" style={{ fontSize: '11px', color: '#8898aa' }}>2–4 leads</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" style={{ gap: '4px' }}>
                                    <Box style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: '#f3f4f6' }} />
                                    <Typography variant="caption" style={{ fontSize: '11px', color: '#8898aa' }}>&lt;2 leads</Typography>
                                </Box>
                            </Box>
                            <Box style={{ marginBottom: '16px', padding: '10px 12px', backgroundColor: '#f6f9fc', borderRadius: '6px', borderLeft: '3px solid #5e72e4' }}>
                                <Typography variant="caption" style={{ display: 'block', fontSize: '12px', color: '#525f7f', lineHeight: 1.6 }}>
                                    <strong>What this shows:</strong> the algorithm&apos;s plan for distributing leads to each buyer this week. Each row is a buyer. The number on the right (e.g. <em>104/300</em>) is <strong>delivered / effective cap</strong> (cap after surge adjustment). County chips show <strong>actual/target</strong> for that county.
                                    <br />
                                    <strong>Effective cap</strong> = weekly cap minus any surge adjustment. This is the real target the system paces to — it&apos;s the same figure used by the waste forecast and marketing actions above.
                                    <br />
                                    <strong>Why targets don&apos;t always sum to the cap:</strong> county targets are proportional to expected lead volume. Counties with very low projected volume get a target of 0 (filled from spillover only). Rounding and zero-target counties mean the sum is approximate.
                                    <br />
                                    <strong>Coverage %</strong> = what share of total weekly lead volume this buyer&apos;s counties represent. High % = competes in many counties. Low % = narrow coverage (more likely to need county ads).
                                </Typography>
                            </Box>
                            {providerNames.map(name => {
                                const p = currentTargets[name]
                                const delivered = p.delivered || 0
                                const remaining = p.remaining || 0
                                const fillPct = p.cap > 0 ? Math.round(delivered / p.cap * 100) : 0
                                const targets = p.targets || {}
                                const countyActuals = p.countyActuals || {}
                                const sortedCounties = Object.entries(targets)
                                    .sort((a, b) => b[1] - a[1])

                                return (
                                    <Box key={name} style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f6f9fc', borderRadius: '6px' }}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: '8px' }}>
                                            <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
                                                <Typography variant="body1" style={{ fontWeight: 700, fontSize: '14px', color: '#32325d' }}>
                                                    {name}
                                                </Typography>
                                                <span style={{
                                                    fontWeight: 700, fontSize: '12px',
                                                    color: p.coverageStrength >= 80 ? '#2dce89' : p.coverageStrength >= 50 ? '#fb6340' : '#f5365c'
                                                }}>
                                                    {p.coverageStrength}% coverage
                                                </span>
                                            </Box>
                                            <Box style={{ textAlign: 'right' }}>
                                                <span style={{ fontWeight: 700, fontSize: '14px', color: fillPct >= 90 ? '#2dce89' : fillPct >= 50 ? '#fb6340' : '#32325d' }}>
                                                    {delivered}/{p.cap}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#adb5bd', marginLeft: '6px' }}>
                                                    ({remaining} left)
                                                </span>
                                            </Box>
                                        </Box>
                                        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {sortedCounties.map(([county, target]) => {
                                                const actual = countyActuals[county] || 0
                                                const roundedTarget = Math.round(target)
                                                return (
                                                    <Chip
                                                        key={county}
                                                        label={`${county}: ${actual}/${roundedTarget}`}
                                                        size="small"
                                                        style={{
                                                            fontSize: '11px',
                                                            fontWeight: 600,
                                                            height: '22px',
                                                            backgroundColor: target >= 5 ? '#e8f5e9' : target >= 2 ? '#fff8e1' : '#f3f4f6',
                                                            color: '#32325d'
                                                        }}
                                                    />
                                                )
                                            })}
                                        </Box>
                                    </Box>
                                )
                            })}
                        </CardContent>
                    </Collapse>
                </Card>
            )}

            {/* County Coverage Map */}
            <Card style={{ marginBottom: '1rem' }}>
                <CardHeader
                    title={
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ cursor: 'pointer' }}
                            onClick={() => setShowCoverage(!showCoverage)}>
                            <Box>
                                <Typography variant="h6" style={{ fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', color: '#32325d' }}>
                                    County Coverage ({coveredCounties.length}/{countyAnalysis.filter(c => c.avgLeads > 0).length} covered)
                                </Typography>
                                <Typography variant="caption" style={{ color: '#adb5bd' }}>
                                    The % of total leads we expect to come from each county organically (based on 6-month historical data ending 9 May 2026)
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
                                <Typography variant="caption" style={{ color: '#adb5bd', fontStyle: 'italic', fontSize: '11px' }}>
                                    Static — based on 6-month organic data ending 9 May 2026
                                </Typography>
                                <IconButton size="small">
                                    {showCoverage ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>
                        </Box>
                    }
                />
                <Collapse in={showCoverage}>
                    <CardContent style={{ padding: '24px 24px 20px' }}>
                        <Box style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '8px'
                        }}>
                            {countyAnalysis
                                .filter(c => c.avgLeads > 0)
                                .sort((a, b) => b.avgLeads - a.avgLeads)
                                .map(c => (
                                    <Box key={c.county} style={{
                                        padding: '8px 12px',
                                        backgroundColor: c.uncovered ? '#fff5f5' : c.soleProvider ? '#fffbea' : '#f6f9fc',
                                        borderRadius: '4px',
                                        borderLeft: `3px solid ${c.uncovered ? '#f5365c' : c.soleProvider ? '#fb6340' : '#2dce89'}`,
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                    }}>
                                        <Box>
                                            <Typography variant="body2" style={{ fontWeight: 600, fontSize: '13px', color: '#32325d' }}>
                                                {c.county}
                                                {c.uncovered && <span style={{ color: '#f5365c', fontSize: '11px', marginLeft: '6px' }}>NO COVER</span>}
                                                {c.soleProvider && <span style={{ color: '#fb6340', fontSize: '11px', marginLeft: '6px' }}>SOLE</span>}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: '#8898aa', fontSize: '11px' }}>
                                                {c.providerCount} provider{c.providerCount !== 1 ? 's' : ''}: {c.providers.join(', ') || 'none'}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" style={{ fontWeight: 700, fontSize: '14px', color: '#32325d', minWidth: '50px', textAlign: 'right' }}>
                                            {totalAvgLeads > 0 ? ((c.avgLeads / totalAvgLeads) * 100).toFixed(1) : 0}%
                                        </Typography>
                                    </Box>
                                ))}
                        </Box>
                    </CardContent>
                </Collapse>
            </Card>
        </Box>
    )
}

export default EnergyAnalyticsPanel
